import React from "react";
import { Outlet, useParams } from "react-router";
import Breadcrumbs from "../../utils/components/Breadcrumbs";
import VerticalTabs from "../../utils/components/VerticalTabs";
import API from "../../utils/API";
import defaultLogo from "../../static/Logo.svg";
import { auth } from "../../App/routes";
import { useProject } from "../../utils/hooks/useProject";
import { useUser } from "../../utils/hooks/useUser";
import { isProjectAdmin } from "../../utils/roles";

const Company = ({ translate }) => {
  const [breadcrumbs, setBreadcrumbs] = React.useState([]);
  const [company, setCompany] = React.useState(null);
  const [companyRoles, setCompanyRoles] = React.useState([]);
  const [users, setUsers] = React.useState([]);
  const [usersLoading, setUsersLoading] = React.useState(true);
  const [totalUsers, setTotalUsers] = React.useState(0);
  const [groups, setGroups] = React.useState([]);
  const [groupsLoading, setGroupsLoading] = React.useState(true);
  const { id } = useParams();

  const { project } = useProject();
  // Logo is part of the shared company record, which a Project Admin cannot edit
  const canChangeLogo = !isProjectAdmin(useUser());

  const handleFileChange = (e) => {
    let file = e.target.files[0];
    let reader = new FileReader();

    reader.onloadend = function () {
      if (reader.result.includes("data:image/")) {
        const image = reader.result.split(",")[1];
        updateCompanyLogo(image);
      }
    };
    reader.readAsDataURL(file);
  };

  const getCompany = React.useCallback(() => {
    API.getCompanyData(id).then(({ data: res }) => {
      setCompany(res.data);
    });
  }, [id]);

  const updateCompanyLogo = (payload) => {
    API.updateCompanyData({ logo: payload }, id).then(({ data: res }) => {
      if (res.data.name) {
        setCompany({ ...company, logo: res.data.logo });
      }
    });
  };

  const getUsers = React.useCallback(
    (arg = {}) => {
      const { sortBy, orderBy, query, page, rowsPerPage } = arg;
      setUsersLoading(true);
      API.getCompanyUsers(
        project?.id,
        id,
        page || 1,
        rowsPerPage || 10,
        query || null,
        sortBy,
        orderBy
      )
        .then(({ data: res }) => {
          setUsers(res.data);
          setTotalUsers(res.pagination.total_count);
          setUsersLoading(false);
        })
        .catch(() => setUsersLoading(false));
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [project?.id, id]
  );

  const getGroups = React.useCallback(() => {
    setGroupsLoading(true);

    API.getCompanyGroups(id, 1, 100, project?.id)
      .then(({ data: res }) => {
        setGroups(res.data);
        setGroupsLoading(false);
      })
      .catch(() => setGroupsLoading(false));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [project?.id, id]);

  const getRoles = React.useCallback(() => {
    if (project?.id) {
      API.getRoles(project?.id, id).then(({ data: res }) => {
        setCompanyRoles(res.data);
      });
    } else {
      setCompanyRoles([]);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [project?.id, id]);

  const refreshData = React.useCallback(
    (page) => {
      getUsers(page);
      getGroups();
      getRoles();
    },
    [getUsers, getGroups, getRoles]
  );

  React.useEffect(() => {
    getCompany();
    refreshData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id, project?.id]);

  return (
    <div className="CompanyDetail">
      <div className="container-fluid">
        <div className="row">
          <div className="col">
            <Breadcrumbs data={breadcrumbs} />
          </div>
        </div>
        <div className="row">
          <div className="col-3 col-lg-2">
            <div className="row p-0">
              <div className="inner-page-sidebar page page-shadow p-0">
                <VerticalTabs
                  headerNode={
                    <div className="col-12">
                      <div className="row">
                        <div className="col-12 d-flex justify-content-center">
                          {company?.logo ? (
                            <img
                              src={company.logo}
                              alt="Company logo"
                              className="logo"
                            />
                          ) : (
                            <img
                              src={defaultLogo}
                              alt="Company logo"
                              className="logo"
                            />
                          )}
                        </div>
                      </div>
                      {canChangeLogo && (
                        <div className="row">
                          <div className="col-12 d-flex justify-content-center">
                            <label
                              htmlFor="logo-input"
                              className="logo-input-label"
                            >
                              {translate("company.change-company-logo")}
                            </label>
                            <input
                              type="file"
                              name="logo"
                              accept="image/*"
                              onChange={handleFileChange}
                              id="logo-input"
                              className="d-none"
                            />
                          </div>
                        </div>
                      )}
                    </div>
                  }
                  nodes={auth.company.pages.map((item) => ({
                    id: item.id,
                    icon: (
                      <div className="svg-icon rounded-circle d-flex justify-content-center align-items-center account-icon text-orange">
                        {item.icon}
                      </div>
                    ),
                    title: item.name,
                    link:
                      auth.company.link.replace(":id", id) +
                      (item.link ? `/${item.link}` : ""),
                  }))}
                />
              </div>
            </div>
          </div>
          <div className="col-9 col-lg-10">
            <section className="page page-shadow">
              <Outlet
                context={{
                  breadcrumbs,
                  setBreadcrumbs,
                  users,
                  totalUsers,
                  groups,
                  refreshData,
                  company,
                  companyRoles,
                  usersLoading,
                  groupsLoading,
                  getUsers,
                }}
              />
            </section>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Company;
